import {
	Button,
	FormControl,
	Grid2,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { useUTLSettingsContext } from "../../../../context/UTLSettingContext";
import { useContextMenuMESF } from "../../../../controls/contextMenu";
import { themeDXT } from "../../../../css/themeMESF";
import { useSearchAssets } from "../../asset/hooks/useSearchAssets";
import { ShiftParameters } from "../models/ShiftParameters";
import { getShiftParameters } from "../repositories/ShiftsCrewsRepository";
import { CreateShift } from "./Create/CreateShift";
import { DeleteShift } from "./Delete/DeleteShift";
import { EditShift } from "./Edit/EditShift";
import { useShiftsOptionsFunctions } from "./customHooks/useShiftsOptionsFunctions";
import { useTableData } from "./customHooks/useTableShift";

const useSearchShifts = (assetId: number | null) => {
	return useQuery<ShiftParameters[], Error>({
		queryKey: ["shifts", assetId],
		queryFn: ({ signal }) => getShiftParameters(assetId, signal),
	});
};

export const TableShiftsCrews = () => {
	const gridRef = useRef<AgGridReact>(null);
	const [error, setError] = useState("");
	const [openModalNew, setOpenModalNew] = useState(false);
	const [openModalEditShift, setOpenModalEditShift] = useState(false);
	const [openModalDeleteShift, setOpenModalDeleteShift] = useState(false);
	const [shiftSelected, setShiftSelected] = useState<ShiftParameters | null>(
		null,
	);

	const {
		state: { settings },
	} = useUTLSettingsContext();
	const defaultAssetId = Number(get(settings, "DefaultAssetId", null));
	const [selectedAssetId, setSelectedAssetId] = useState<number | null>(
		defaultAssetId || null,
	);
	const { data: assets } = useSearchAssets();
	const assetsList = useMemo(
		() => assets?.filter((a) => a.CanBeDefaultAsset) ?? [],
		[assets],
	);
	const selectedAssetName = useMemo(
		() =>
			assetsList.find((a) => a.AssetId === selectedAssetId)?.AssetName ?? "",
		[assetsList, selectedAssetId],
	);

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		refetch,
	} = useSearchShifts(selectedAssetId);

	const formattedRows = rows?.map(
		({
			PatternStart,
			ShiftCodes,
			Crews,
			PatternDays,
			CrewRotation,
			Comments,
			CanEdit,
		}: ShiftParameters) => ({
			id: ShiftCodes,
			PatternStart,
			ShiftCodes,
			Crews,
			PatternDays,
			CrewRotation,
			Comments,
			CanEdit,
		}),
	);

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const { getMenuOptions } = useShiftsOptionsFunctions({
		setShiftId: setShiftSelected,
		setShowCreateModal: setOpenModalNew,
		setShowDeleteModal: setOpenModalDeleteShift,
		setShowEditModal: setOpenModalEditShift,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		setShiftSelected,
		setOpenModalEditShift,
		setOpenModalDeleteShift,
		showContextMenu,
	});

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableShifts");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableShifts",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Shifts / Crew
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<FormControl size="small" sx={{ minWidth: 200 }}>
						<InputLabel>Asset</InputLabel>
						<Select
							value={selectedAssetId ?? ""}
							onChange={(e) => setSelectedAssetId(Number(e.target.value))}
							label="Asset"
						>
							{assetsList.map((asset) => (
								<MenuItem key={asset.AssetId} value={asset.AssetId}>
									{asset.AssetName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 container size={{ md: 12, xs: 12 }}>
						<Grid2
							component={Paper}
							size={{ md: 12, xs: 12 }}
							style={{
								height: "70vh",
							}}
						>
							<AgGridReact
								loading={isLoading}
								gridOptions={{
									theme: themeDXT,
								}}
								ref={gridRef}
								rowData={formattedRows || []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows
								loadingOverlayComponent={CenteredLazyLoading}
								getContextMenuItems={(e: any) => getContextMenuItems(e)}
								rowSelection="single"
								onRowDoubleClicked={(event) => {
									setShiftSelected(event.data as ShiftParameters);
									setOpenModalEditShift(true);
								}}
							/>
						</Grid2>
						<Grid2 container justifyContent="flex-end">
							<Grid2 size={{ md: 2, xs: 12 }}>
								<Button
									variant="contained"
									color="primary"
									onClick={() => setOpenModalNew(!openModalNew)}
									fullWidth
								>
									NEW SHIFT
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>
			<CreateShift
				shiftSelected={shiftSelected}
				show={openModalNew}
				onHide={(shouldUpdate: boolean) => {
					setOpenModalNew(false);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Shifts / Crew"
				assetId={selectedAssetId}
				assetName={selectedAssetName}
			/>
			<EditShift
				shiftSelected={shiftSelected}
				show={openModalEditShift}
				onHide={(shouldUpdate: boolean) => {
					setOpenModalEditShift(false);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Shifts / Crew"
				assetId={selectedAssetId}
				assetName={selectedAssetName}
			/>
			<DeleteShift
				shiftSelected={shiftSelected}
				show={openModalDeleteShift}
				onHide={(shouldUpdate: boolean) => {
					setOpenModalDeleteShift(false);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Shifts / Crew"
				assetId={selectedAssetId}
				assetName={selectedAssetName}
			/>
		</>
	);
};
